import { Link } from "react-router-dom";
import {
  ArrowRight, Search, MapPin, MailCheck, ShieldCheck, Map, FileText, ScanLine,
} from "lucide-react";
import Seo from "@/components/Seo";
import CitySkylinePanel from "@/components/CitySkylinePanel";
import TduTerritoryMap from "@/components/TduTerritoryMap";
import { getStateByCode, hasFeature, statePath } from "@/lib/stateRegistry";
import { PUBLIC_LAUNCH_STATE_CODE } from "@/lib/displayFlags";
import useMinWidth from "@/hooks/useMinWidth";
import NewsletterSignupBlock from "@/components/NewsletterSignupBlock";

/**
 * Decorative hero photo composite -- Hero Background Design.md, the single ratified exception
 * to Design Specification.md Section 3.5 ("No Stock Photography At Launch"), scoped to this
 * page's hero only. Self-hosted photos (never hot-linked):
 *   - City panel: Houston skyline + the "Be Someone" bridge (Elysian Viaduct over I-45) --
 *     as of 2026-09-29, an Owner-supplied image of unverified original source/license under a
 *     named, one-time founder override of this section's licensed-photography rule (see the
 *     "Founder Override" note in Design Specification.md Section 3.5 and the credit field in
 *     frontend/src/lib/citySkylines.js for the full record). The prior Wikimedia CC BY 3.0 photo
 *     it replaced is documented in git history, not here.
 *   - Residential panel: "Aerial View Of Residential Area At Twilight", Przemyslaw Sel, Pexels License.
 *   - Family badge (phone only, see below): "Family Riding On Bicycle", Brett Sayles, Pexels License.
 * Rendered as foreground <img> (not CSS background) for fetchpriority/loading control per
 * Section 4.2's LCP requirement; alt="" throughout and the whole band is aria-hidden/presentation
 * per Section 4.4, since the H1 and support text already carry the page's meaning.
 * Desktop/tablet (>=768px) show the two-panel diagonal-blend split -- residential community on
 * the LEFT (closer, "treetop" framing), city skyline on the RIGHT (further back, "helicopter"
 * framing); phone (<768px) swaps to one flattened single-panel composite (same left/right
 * arrangement, pre-blended into one image since mobile has no room for a CSS mask split) with the
 * family photo as a small circular badge next to the subhead text (Section 4.3) -- NOT layered
 * over either photo panel.
 * useMinWidth renders only one image set at a time so a phone visitor never downloads the desktop
 * panels, and vice versa. The city (right) panel is rendered via <CitySkylinePanel city="houston" />
 * (frontend/src/components/CitySkylinePanel.jsx) rather than a hardcoded image path -- see that
 * component and frontend/src/lib/citySkylines.js for why: a planned follow-up page needs this
 * same panel pattern for other Texas cities, and this keeps the CSS/markup pattern defined once.
 * The homepage hero itself is unaffected by that refactor -- it always renders Houston.
 *
 * See git history for the full photo-grading provenance notes (2026-09-24 revisions).
 *
 * 2026-09-30 reduced-scope early launch (Owner-approved, Witherspoon-CLO compliance/
 * 01_texas_disclosures.md Section 17; Reba-CPO product scope): the ZIP-search form that used to
 * sit over this photo band is retired -- ESID lookup and Bill-Check are the site's entire live
 * value prop now, so they are the hero's two full-weight primary CTAs (see HeroActions below),
 * not a form plus two subordinate quick-link chips. The photo composite itself, its breakpoint
 * behavior and accessibility treatment are unchanged.
 */
const HERO_DIMS = {
  "residential-panel": [1400, 833],
  "hero-phone": [900, 620],
  "family-badge": [160, 160],
};

function HeroPicture({ base, className, testId, eager = true }) {
  const [w, h] = HERO_DIMS[base];
  return (
    <picture>
      <source srcSet={`/hero/${base}.webp`} type="image/webp" />
      <img
        src={`/hero/${base}.jpg`}
        alt=""
        width={w}
        height={h}
        className={className}
        loading={eager ? "eager" : undefined}
        fetchPriority={eager ? "high" : undefined}
        data-testid={testId}
      />
    </picture>
  );
}

function HeroPhotoComposite({ isTabletUp }) {
  return (
    <div className="hero-photo-band" aria-hidden="true" role="presentation" data-testid="hero-photo-band">
      {isTabletUp ? (
        <div className="hero-photo-split">
          <HeroPicture base="residential-panel" className="hero-photo-residential" testId="hero-panel-residential" />
          <CitySkylinePanel city="houston" className="hero-photo-city" testId="hero-panel-city" />
        </div>
      ) : (
        <HeroPicture base="hero-phone" className="hero-photo-phone" testId="hero-panel-phone" />
      )}
      <div className="hero-scrim" />
      <div className="hero-radial-darken" />
    </div>
  );
}

function HeroFamilyBadge() {
  return <HeroPicture base="family-badge" className="hero-family-badge" testId="hero-family-badge" eager />;
}

/**
 * The hero's two full-weight primary CTAs (2026-09-30 reduced-scope launch). Replaces the retired
 * ZIP-search form: ESID lookup and Bill-Check ARE the product right now, so both get equal, full
 * visual weight -- not a primary form with subordinate link chips underneath it. Same icon-tile/
 * rotate-on-hover signature used by the Step and Helpful-Tools cards elsewhere on this page, so the
 * hero reads as the same design language, not a bespoke one-off.
 */
function HeroActions({ esidHref }) {
  const actions = [
    {
      to: esidHref,
      icon: ScanLine,
      title: "Find my ESID",
      d: "Your Electric Service ID, straight from ERCOT's registry.",
      testId: "hero-cta-esid",
    },
    {
      to: "/bill-check",
      icon: FileText,
      title: "Check my bill",
      d: "Upload your bill for a plain-language read on your rate.",
      testId: "hero-cta-bill-check",
    },
  ];
  return (
    <div className="mt-8 flex flex-col sm:flex-row gap-4 max-w-xl" data-testid="hero-actions">
      {actions.map((a) => (
        <Link
          key={a.testId}
          to={a.to}
          className="group/cta flex-1 flex items-center gap-4 bg-white rounded-2xl rounded-bl-md shadow-xl ring-1 ring-black/5 p-5 hover:shadow-2xl hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#16A34A] transition-all duration-200"
          data-testid={a.testId}
        >
          <span
            aria-hidden="true"
            className="w-14 h-14 shrink-0 rounded-2xl bg-[#DCFCE7] flex items-center justify-center transition-transform duration-300 group-hover/cta:-rotate-6 group-focus-visible/cta:-rotate-6"
          >
            <a.icon className="w-6 h-6 text-[#15803D]" />
          </span>
          <span className="min-w-0">
            <span className="block font-display text-base font-bold text-[#0A2540]">{a.title}</span>
            <span className="block text-sm text-slate-600 leading-snug mt-0.5">{a.d}</span>
          </span>
          <ArrowRight className="w-5 h-5 text-[#16A34A] shrink-0 ml-auto transition-transform group-hover/cta:translate-x-1" aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

const STEPS = [
  { n: "1", icon: ScanLine, t: "Look up your ESID", d: "Enter your address and we pull your Electric Service ID straight from ERCOT's public registry." },
  { n: "2", icon: FileText, t: "Upload your bill", d: "We read your rate, usage, and provider from the bill itself, then explain it in plain language." },
  { n: "3", icon: MapPin, t: "Find your utility", d: "See which utility delivers power to your address, and who to call if it goes out." },
];

const FEATURES = [
  { icon: ShieldCheck, t: "No sales pitch", d: "ESID lookup and bill-check are free tools, not a funnel toward a sales call." },
  { icon: Search, t: "Sourced straight from ERCOT", d: "Your ESID comes from ERCOT's own public registry — never guessed or generated." },
  { icon: MailCheck, t: "Free, no account needed", d: "Use either tool as many times as you want. No signup, no obligation." },
];

/**
 * National landing page. Texas-only at launch (Owner scope, 2026-09-22 and 2026-09-24):
 * no state-picker chrome anywhere a visitor can see it -- the site is Texas-only for now,
 * so "choose/change your state" language and affordances do not exist here or in Header/
 * Footer/NotFound/HowItWorks. STATE_REGISTRY and the state-subdirectory routing underneath
 * this are untouched (multi-state expansion is still the long-term plan) -- this is a copy/
 * chrome change on top of that architecture, not a routing change. No statistics, ratings,
 * testimonials or savings figures: none are substantiated (Contract 6, launch gate 6).
 *
 * 2026-09-30 reduced-scope early launch (Owner-approved, Witherspoon-CLO compliance/
 * 01_texas_disclosures.md Section 17; Reba-CPO product scope doc): ESID lookup and Bill-Check
 * are the site's entire live value prop for this phase. Removed entirely: the ZIP-search hero
 * form (no plan-shopping intake anywhere on this page), the live Provider Grid + sample
 * PlanCards section ("Paying too much...We'll compare and help you save"), and the "What we
 * show" EFL/pricing facts section -- all three either promise or perform plan comparison, which
 * is disabled for this phase, not just unlinked. The "Three simple steps" and "Why RateCurrent"
 * sections are rewritten around what the site actually does today (ESID, bill-check, utility
 * lookup) instead of a ZIP-to-enrollment funnel. The TDU territory map -- a real, live feature --
 * moves up to sit directly under the hero.
 */
export default function Home() {
  const launchState = getStateByCode(PUBLIC_LAUNCH_STATE_CODE);
  const isTabletUp = useMinWidth(768); // Hero Background Design.md Section 4.3 breakpoint

  return (
    <div data-testid="home-page">
      <Seo
        title="Texas ESID Lookup & Electric Bill Explainer"
        description="Find your Texas ESID from ERCOT's public registry and get a plain-English explanation of your electric bill. Free, no account needed."
        path="/"
      />
      <section className="relative overflow-hidden min-h-[42vh] md:min-h-[480px] lg:min-h-[600px]" data-testid="home-hero">
        <HeroPhotoComposite isTabletUp={isTabletUp} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 lg:px-10 pt-16 pb-12">
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight font-extrabold leading-[1.05] text-white max-w-full md:max-w-[18ch]">
            Everything you need to know before you shop for Texas electricity.
          </h1>
          <div className={isTabletUp ? "" : "mt-6 flex items-center gap-3"}>
            {!isTabletUp && <HeroFamilyBadge />}
            <p className={`text-lg md:text-xl text-white/95 max-w-xl leading-relaxed ${isTabletUp ? "mt-6" : ""}`}>
              Look up your ESID. Upload your energy bill for a simple, easy-to-understand breakdown.
            </p>
          </div>

          {launchState && <HeroActions esidHref={statePath(launchState.slug, "esid-lookup")} />}
        </div>
      </section>

      {/* TDU territory map embed (Ive-WBD placement spec, 2026-09-30; moved higher 2026-09-30
          reduced-scope launch since it's a real, live feature and the sections that used to sit
          above it -- Provider Grid and sample plans -- are cut). Shares
          frontend/src/components/TduTerritoryMap.jsx with Utilities.jsx (variant="home" trims the
          detail panel) so the territory geometry and selection behavior can never drift between
          the two pages. */}
      {launchState && hasFeature(launchState, "tdu_content") && (
        <section className="border-t border-slate-100" data-testid="home-tdu-map-section">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 md:py-32">
            <div className="max-w-2xl mb-14">
              <span className="pill mb-5"><Map className="w-3.5 h-3.5" /> Utility map</span>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight text-[#0A2540]">
                Who delivers power to your address?
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Your utility owns the wires and responds to outages; your retail provider sells the electricity. Here&rsquo;s which one serves your area.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <TduTerritoryMap variant="home" />
            </div>
            <div className="mt-8">
              <Link
                to={statePath(launchState.slug, "utilities")}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#16A34A] hover:underline"
                data-testid="home-tdu-map-full-link"
              >
                See the full utility directory, contact numbers &amp; FAQs <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Flag-gated (REACT_APP_NEWSLETTER_SIGNUP_ENABLED). Owner order 2026-10-13: sits directly above the How it works section. */}
      <NewsletterSignupBlock placement="home" />

      <section className="border-t border-slate-100 bg-[#F7F9FC]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="pill mb-5">How it works</span>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-tight text-[#0A2540]">
              Three simple steps to understand your account
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              No spreadsheets, no fine print you have to translate yourself.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                className={`group bg-white rounded-3xl border border-slate-100 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${["", "md:mt-10", "md:mt-4"][i]}`}
                data-testid={`home-step-${s.n}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#DCFCE7] flex items-center justify-center transition-transform duration-300 group-hover:-rotate-6">
                    <s.icon className="w-6 h-6 text-[#15803D]" aria-hidden="true" />
                  </div>
                  <span className="font-display font-bold text-3xl text-slate-200">{s.n}</span>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#0A2540] mb-2">{s.t}</h3>
                <p className="text-slate-600 leading-relaxed">{s.d}</p>
                <span className="mt-5 block h-[3px] w-8 rounded-full bg-[#16A34A] transition-all duration-300 group-hover:w-14" aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-100">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative" aria-hidden="true">
            <div
              className="rounded-3xl p-10 h-[420px] flex flex-col justify-between"
              style={{ background: "linear-gradient(160deg, #0A2540 0%, #15803D 100%)" }}
            >
              <ShieldCheck className="w-10 h-10 text-white" />
              <div>
                <p className="font-display text-3xl font-extrabold text-white">From ERCOT's registry</p>
                <p className="mt-2 text-sm text-white/80">Every ESID traces back to ERCOT&rsquo;s own public registry — not a guess, not generated.</p>
              </div>
            </div>
            {/* Overlapping badge -- deliberate editorial "sticker" placement, protruding past the
                panel's own corner, echoing the hero's overlapping-panel composition as a repeating
                asymmetric-layout signature. Desktop only; on a narrow phone width there is no room
                for a floating element to overlap without risking clipping. */}
            <div className="hidden md:block absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl border border-slate-100 px-5 py-3 rotate-[-4deg]">
              <p className="text-overline text-[#15803D]">Free</p>
              <p className="text-xs text-slate-500 mt-0.5">no account needed</p>
            </div>
          </div>
          <div className="md:col-span-7">
            <span className="pill mb-5">Why RateCurrent</span>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-tight text-[#0A2540]">
              Built to explain.<br />Not to sell.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              RateCurrent reads the same public records and disclosures {launchState?.name ?? "Texas"} utilities and ERCOT already publish, and hands you the plain-language version.
            </p>
            <ul className="mt-8 space-y-5">
              {FEATURES.map((f) => (
                <li key={f.t} className="flex gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#DCFCE7] flex items-center justify-center shrink-0">
                    <f.icon className="w-5 h-5 text-[#15803D]" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-semibold text-base text-[#0A2540]">{f.t}</p>
                    <p className="text-slate-600 text-sm mt-1">{f.d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/how-it-works" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#16A34A] hover:underline mt-10" data-testid="home-why-cta">
              See how it works <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {launchState && (
        <section className="border-t border-slate-100">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 md:py-32">
            <div className="bg-[#0A2540] text-white rounded-3xl p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#16A34A] opacity-20 rounded-full blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#16A34A] opacity-10 rounded-full blur-3xl" aria-hidden="true" />
              <div className="lg:col-span-8 relative">
                <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
                  Know your account,<br /><span className="text-[#4ADE80]">before you shop.</span>
                </h2>
                <p className="mt-5 text-zinc-300 text-lg max-w-[55ch]">
                  Look up your ESID. Upload your energy bill for a simple, easy-to-understand breakdown.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 relative">
                <Link to={statePath(launchState.slug, "esid-lookup")} className="btn-primary justify-center" data-testid="home-cta-esid">
                  Find my ESID <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </Link>
                <Link
                  to="/bill-check"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-[1.5px] border-white text-white font-semibold text-sm px-6 py-3.5 hover:bg-white hover:text-[#0A2540] transition-colors"
                  data-testid="home-cta-bill-check"
                >
                  Check my bill <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link
                  to={statePath(launchState.slug, "plans")}
                  className="text-center text-sm text-zinc-300 underline underline-offset-2 hover:text-white mt-1"
                  data-testid="home-cta-waitlist"
                >
                  Plan comparison is coming soon. Get notified
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

    </div>
  );
}
